<template>
  <div class="event-list-page">
    <div class="page-header">
      <h2 class="page-title">AI 多變數事件紀錄</h2>
      <!--el-tag
        v-if="isUsingMockData"
        type="warning"
        size="small"
        effect="dark"
        class="datasource-tag"
      >
        模擬資料展示中
      </el-tag>
      <el-tag
        v-else
        type="success"
        size="small"
        effect="dark"
        class="datasource-tag"
      >
        即時資料介接中
      </el-tag-->
    </div>

    <el-tabs v-model="activeTab" class="event-tabs">
      <el-tab-pane label="儀器事件紀錄" name="instrument">
        <el-card shadow="never" class="filter-card">
          <el-row :gutter="20" class="filter-row">
            <el-col :xs="24" :sm="9" :md="9">
              <div class="filter-item">
                <span class="filter-label">選擇儀器：</span>
                <el-cascader
                  v-model="form.instrument"
                  style="width: 100%"
                  ref="cascader"
                  filterable
                  clearable
                  :options="instrumentOptions"
                  :props="cascaderProps"
                  :collapse-tags="true"
                  :show-all-levels="false"
                  placeholder="未選擇時查詢全部儀器"
                />
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12">
              <div class="filter-item">
                <span class="filter-label">查詢時間：</span>
                <el-date-picker
                  v-model="form.date"
                  type="datetimerange"
                  style="width: 100%"
                  format="yyyy/MM/dd HH:mm"
                  value-format="timestamp"
                  start-placeholder="開始時間"
                  end-placeholder="結束時間"
                  :picker-options="datePickerOptions"
                  :default-time="['00:00:00', '23:59:59']"
                />
              </div>
            </el-col>
            <el-col :xs="24" :sm="3" :md="3" class="filter-btn-col">
              <el-button type="primary" size="small" icon="el-icon-search" @click="fetchAlarms">查詢</el-button>
            </el-col>
          </el-row>
        </el-card>

        <el-card shadow="hover" class="table-card">
          <div class="table-toolbar">
            <div class="table-toolbar__left">
              <el-radio-group v-model="filterType" size="small">
                <el-radio label="all">全部</el-radio>
                <el-radio label="alert" :disabled="!hasAlertData">警戒值</el-radio>
                <el-radio label="action" :disabled="!hasActionData">行動值</el-radio>
                <el-radio label="alarm" :disabled="!hasAlarmData">預判值</el-radio>
              </el-radio-group>
            </div>
            <div class="table-toolbar__right">
              <el-input
                v-model="searchText"
                placeholder="搜索儀器種類..."
                prefix-icon="el-icon-search"
                clearable
                size="small"
                style="width: 200px"
              />
              <el-button
                size="small"
                icon="el-icon-download"
                style="margin-left: 8px"
                @click="exportExcel"
              >匯出 Excel</el-button>
            </div>
          </div>

          <el-table
            v-loading="isLoading"
            :data="pagedData"
            border
            stripe
            style="width: 100%"
            :row-class-name="tableRowClassName"
            :header-cell-style="headerCellStyle"
            size="small"
          >
            <el-table-column
              prop="probeName"
              label="儀器種類"
              min-width="180"
              show-overflow-tooltip
            />

            <el-table-column label="警戒值" min-width="80">
              <template slot-scope="scope">
                {{ formatAlValue(scope.row) }}
              </template>
            </el-table-column>

            <el-table-column label="行動值" min-width="80">
              <template slot-scope="scope">
                {{ formatWslValue(scope.row) }}
              </template>
            </el-table-column>

            <el-table-column label="發生時間" min-width="150">
              <template slot-scope="scope">
                {{ formatTime(scope.row.timestamp) }}
              </template>
            </el-table-column>

            <el-table-column label="當日第N次" min-width="90" align="center">
              <template slot-scope="scope">
                <el-tag
                  v-if="dayOccurrenceMap[scope.row._id] && dayOccurrenceMap[scope.row._id].total > 1"
                  size="mini"
                  type="warning"
                  effect="plain"
                >
                  第 {{ dayOccurrenceMap[scope.row._id].nth }} 次
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="類型預判" min-width="150">
              <template slot-scope="scope">
                <div class="alarm-type-cell">
                  <div class="alarm-type-cell__header">
                    <span class="alarm-type-name">
                      {{ getAlarmTypeName(scope.row.alarmType) }}
                    </span>
                    <el-popover
                      v-if="scope.row.alarmType && hasRowData(scope.row)"
                      trigger="hover"
                      placement="right"
                      width="380"
                    >
                      <EventDetailPopover :row="scope.row" />
                      <i
                        slot="reference"
                        class="el-icon-info info-trigger"
                        :class="{ 'info-trigger--has-data': hasRowData(scope.row) }"
                      />
                    </el-popover>
                  </div>
                  <AlarmTypeDisplay
                    v-if="scope.row.alarmType"
                    :alarm-type="scope.row.alarmType"
                  />
                </div>
              </template>
            </el-table-column>

            <el-table-column label="通報狀態" min-width="80">
              <template slot-scope="scope">
                <el-tag
                  v-if="scope.row.status"
                  :type="scope.row.status === '已通報' ? 'success' : 'info'"
                  size="mini"
                  effect="plain"
                >
                  {{ scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="通報時間" min-width="150">
              <template slot-scope="scope">
                {{ formatTime(scope.row.time) }}
              </template>
            </el-table-column>

            <el-table-column label="通報人員" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.name || '' }}
              </template>
            </el-table-column>

            <el-table-column label="通報" width="90" align="center">
              <template slot-scope="scope">
                <el-button
                  v-if="scope.row.status !== '已通報'"
                  size="mini"
                  type="warning"
                  plain
                  @click="openReportDialog(scope.row)"
                >
                  通報
                </el-button>
                <span v-else class="reported-label">已通報</span>
              </template>
            </el-table-column>

            <el-table-column label="圖表" width="90" align="center">
              <template slot-scope="scope">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  @click="goToChart(scope.row)"
                >
                  chart
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-bar">
            <el-pagination
              background
              layout="total, prev, pager, next, jumper"
              :total="filteredData.length"
              :current-page.sync="currentPage"
              :page-size="pageSize"
            />
          </div>

          <!-- 通報確認 Dialog -->
          <el-dialog
            title="確認通報"
            :visible.sync="reportDialog.visible"
            width="400px"
            :close-on-click-modal="false"
          >
            <div v-if="reportDialog.row" class="report-dialog__body">
              確認對以下儀器事件進行通報？
              <div class="report-dialog__probe">{{ reportDialog.row.probeName }}</div>
              <div class="report-dialog__time">發生時間：{{ formatTime(reportDialog.row.timestamp) }}</div>
            </div>
            <span slot="footer">
              <el-button @click="reportDialog.visible = false">取消</el-button>
              <el-button
                type="primary"
                :loading="reportDialog.loading"
                @click="confirmReport"
              >
                確認通報
              </el-button>
            </span>
          </el-dialog>

          <!-- 圖示說明 -->
          <div class="icon-legend">
            <h4 class="icon-legend__title">圖示說明</h4>
            <div class="icon-legend__items">
              <div class="icon-legend__item">
                <StatusIcon type="continuous" title="連續" />
                <span class="icon-legend__text">連續</span>
              </div>
              <div class="icon-legend__item">
                <StatusIcon type="rain" title="降雨" />
                <span class="icon-legend__text">降雨</span>
              </div>
              <div class="icon-legend__item">
                <StatusIcon type="simultaneous" title="同時" />
                <span class="icon-legend__text">同時</span>
              </div>
              <div class="icon-legend__item">
                <StatusIcon type="temp" title="溫度效應" />
                <span class="icon-legend__text">溫度</span>
              </div>
              <div class="icon-legend__item">
                <StatusIcon type="recovered" title="已回復" />
                <span class="icon-legend__text">已回復</span>
              </div>
              <div class="icon-legend__item">
                <StatusIcon type="not-recovered" title="未回復" />
                <span class="icon-legend__text">未回復</span>
              </div>
              <div class="icon-legend__item">
                <StatusIcon type="spike" title="突波" />
                <span class="icon-legend__text">突波</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import StatusIcon from '@/components/event/StatusIcon.vue';
import AlarmTypeDisplay from '@/components/event/AlarmTypeDisplay.vue';
import EventDetailPopover from '@/components/event/EventDetailPopover.vue';
import alarmTypeMap from '@/utils/alarmTypeMap';
import { formatTimestamp, getUnit } from '@/utils/formatters';

export default {
  name: 'AiEventList',
  components: {
    StatusIcon,
    AlarmTypeDisplay,
    EventDetailPopover,
  },
  data() {
    return {
      activeTab: 'instrument',
      filterType: 'all',
      searchText: '',
      isLoading: false,
      instrumentOptions: [],
      instrumentNameToIdMap: {},
      cascaderProps: {
        multiple: true,
        label: 'label',
        value: 'value',
      },
      form: {
        instrument: null,
        date: [
          this.$moment().subtract(6, 'month').startOf('day').format('x') * 1,
          this.$moment().endOf('day').format('x') * 1,
        ],
      },
      events: [],
      currentPage: 1,
      pageSize: 10,
      reportDialog: {
        visible: false,
        row: null,
        loading: false,
      },
      datePickerOptions: {
        shortcuts: [
          {
            text: '最近一週',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近一個月',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近三個月',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
              picker.$emit('pick', [start, end]);
            },
          },
        ],
      },
    };
  },
  computed: {
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
    filteredData() {
      let result = this.events;

      if (this.searchText.trim()) {
        const text = this.searchText.toLowerCase().trim();
        result = result.filter(row => row.probeName && row.probeName.toLowerCase().includes(text));
      }

      return result.filter(row => {
        if (this.filterType === 'all') return true;
        if (this.filterType === 'alert') return row.al !== null;
        if (this.filterType === 'action') return row.wsl !== null || (row.wsl === null && row.al === null);
        if (this.filterType === 'alarm') return row.alarmType !== null;
        return true;
      });
    },
    hasAlertData() {
      return this.events.some(row => row.al !== null);
    },
    hasActionData() {
      return (
        this.events.some(row => row.wsl !== null) ||
        this.events.some(row => row.wsl === null && row.al === null)
      );
    },
    hasAlarmData() {
      return this.events.some(row => row.alarmType !== null);
    },
    pagedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredData.slice(start, start + this.pageSize);
    },
    dayOccurrenceMap() {
      const groups = {};
      this.events.forEach(event => {
        if (!event.timestamp || !event.probeName) return;
        const date = new Date(new Date(event.timestamp).getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
        const key = `${event.probeName}_${event.alarmType}_${date}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(event);
      });
      const map = {};
      Object.values(groups).forEach(group => {
        group.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        group.forEach((event, i) => {
          map[event._id] = { nth: i + 1, total: group.length };
        });
      });
      return map;
    },
  },
  watch: {
    filteredData() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.initPage();
  },
  methods: {
    async initPage() {
      if (!this.selectedProject) {
        setTimeout(() => {
          this.initPage();
        }, 500);
        return;
      }

      await this.fetchInstrumentOptions();
      this.fetchAlarms();
    },
    async fetchInstrumentOptions() {
      try {
        const res = await this.$axios({
          category: 'project',
          method: 'get',
          apiName: `${this.selectedProject._id}/area-station-instrument`,
        });
        const project = res.data;
        const nameToIdMap = {};
        this.instrumentOptions = (project.area || []).map(area => ({
          label: area.name,
          value: area._id,
          children: (area.station || []).map(station => ({
            label: station.name,
            value: station._id,
            children: (station.instrument || []).map(ins => {
              const name = ins.latestVersion?.name || ins._id;
              nameToIdMap[name] = ins._id;
              return {
                label: name,
                value: ins._id,
              };
            }),
          })),
        }));
        this.instrumentNameToIdMap = nameToIdMap;
      } catch (err) {
        console.warn('無法載入儀器清單', err);
      }
    },
    async fetchAlarms() {
      this.isLoading = true;
      this.events = [];
      this.isUsingMockData = false;

      const instrumentIds = (this.form.instrument || []).map(path => path[2]).filter(Boolean);
      const [startTime, endTime] = this.form.date;

      try {
        const res = await this.$axios({
          category: 'event',
          method: 'get',
          apiName: '/alerts/alarms',
          params: { instrumentIds, startTime, endTime },
        });

        this.events = (res.data || []).map(event => ({
          _id: event._id,
          instrumentId: event.instrument_id,
          probeName: event.probeName,
          timestamp: event.TIMESTAMP,
          al: event.al,
          wsl: event.wsl,
          alarmType: event.alarmType,
          alarmDesc: event.alarmDesc,
          relatedAnomalyProbeNames: event.relatedAnomalyProbeNames || [],
          i3: event.i3,
          r24: event.r24,
          status: event.reportedAt ? '已通報' : null,
          time: event.reportedAt || null,
          name: event.reportedBy?.name || null,
        }));
      } catch (err) {
        console.warn('fetchAlarms error:', err);
      } finally {
        this.isLoading = false;
      }
    },
    handleFilterChange() {
      this.fetchAlarms();
    },
    formatTime(timestamp) {
      return formatTimestamp(timestamp);
    },
    formatAlValue(row) {
      if (row.al === null || row.al === undefined) return '';
      const unit = getUnit(row.probeName);
      return `${row.al.toFixed(3)} ${unit}`.trim();
    },
    formatWslValue(row) {
      if (row.wsl === null || row.wsl === undefined) return '';
      const unit = getUnit(row.probeName);
      return `${row.wsl.toFixed(3)} ${unit}`.trim();
    },
    getAlarmTypeName(alarmType) {
      if (!alarmType) return '';
      const info = alarmTypeMap[alarmType];
      return info ? info.name : '';
    },
    hasRowData(row) {
      return (
        row.i3 !== null ||
        row.r24 !== null ||
        row.wsl !== null ||
        row.al !== null ||
        (row.relatedAnomalyProbeNames && row.relatedAnomalyProbeNames.length > 0)
      );
    },
    openReportDialog(row) {
      this.reportDialog.row = row;
      this.reportDialog.visible = true;
    },
    async confirmReport() {
      const row = this.reportDialog.row;
      this.reportDialog.loading = true;
      try {
        const res = await this.$axios({
          category: 'event',
          method: 'patch',
          apiName: `/alerts/alarms/${row._id}/report`,
        });
        const idx = this.events.findIndex(e => e._id === row._id);
        if (idx !== -1) {
          this.$set(this.events, idx, {
            ...this.events[idx],
            status: '已通報',
            time: res.data.reportedAt,
            name: res.data.reportedBy?.name || '',
          });
        }
        this.reportDialog.visible = false;
        this.$notify({ title: '成功', message: '通報完成', type: 'success', duration: 3000 });
      } catch (err) {
        this.$notify({ title: '錯誤', message: '通報失敗，請稍後再試', type: 'error', duration: 5000 });
      } finally {
        this.reportDialog.loading = false;
      }
    },
    goToChart(row) {
      if (!this.selectedProject) return;
      const relatedIds = (row.relatedAnomalyProbeNames || [])
        .map(name => this.instrumentNameToIdMap[name])
        .filter(Boolean);
      const instrumentIds = [...new Set([row.instrumentId, ...relatedIds])];
      const routeData = this.$router.resolve({
        path: '/chart',
        query: {
          instrument: instrumentIds.join(','),
          startDate: row.timestamp,
          autoRun: true,
        },
      });
      window.open(routeData.href, '_blank');
    },
    async exportExcel() {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('AI多變數事件紀錄');

      sheet.columns = [
        { header: '儀器種類', key: 'probeName', width: 30 },
        { header: '警戒值', key: 'al', width: 15 },
        { header: '行動值', key: 'wsl', width: 15 },
        { header: '發生時間', key: 'timestamp', width: 22 },
        { header: '當日第N次', key: 'nth', width: 12 },
        { header: '類型預判', key: 'alarmType', width: 16 },
        { header: '告警描述', key: 'alarmDesc', width: 30 },
        { header: '通報狀態', key: 'status', width: 10 },
        { header: '通報時間', key: 'time', width: 22 },
        { header: '通報人員', key: 'name', width: 20 },
      ];

      sheet.getRow(1).font = { bold: true };

      this.filteredData.forEach(row => {
        const occ = this.dayOccurrenceMap[row._id];
        sheet.addRow({
          probeName: row.probeName || '',
          al: this.formatAlValue(row),
          wsl: this.formatWslValue(row),
          timestamp: this.formatTime(row.timestamp),
          nth: occ && occ.total > 1 ? `第 ${occ.nth} 次` : '',
          alarmType: this.getAlarmTypeName(row.alarmType),
          alarmDesc: row.alarmDesc || '',
          status: row.status || '',
          time: this.formatTime(row.time),
          name: row.name || '',
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const filename = `AI多變數事件_${this.$moment().format('YYYYMMDD_HHmm')}.xlsx`;
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), filename);
    },
    tableRowClassName({ row }) {
      if (row.wsl === null && row.al === null) {
        return 'row--no-value';
      }
      return '';
    },
    headerCellStyle() {
      return {
        backgroundColor: '#f5f7fa',
        color: '#606266',
        fontWeight: '600',
        fontSize: '12px',
      };
    },
  },
};
</script>

<style scoped>
.event-list-page {
  padding: 0px;
  background-color: #f8fafc;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.datasource-tag {
  border-radius: 12px;
  font-weight: 600;
}

.filter-card {
  margin-bottom: 0px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px 0;
}

.filter-item {
  display: flex;
  align-items: center;
  width: 100%;
}

.filter-btn-col {
  display: flex;
  align-items: center;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  min-width: 75px;
}

.table-card {
  margin-bottom: 20px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 10px;
}

.table-toolbar__left {
  display: flex;
  align-items: center;
}

.table-toolbar__right {
  display: flex;
  align-items: center;
}

.alarm-type-cell {
  display: flex;
  flex-direction: column;
}

.alarm-type-cell__header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.alarm-type-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
}

.info-trigger {
  font-size: 14px;
  color: #cbd5e1;
  cursor: pointer;
  transition: color 0.2s;
}

.info-trigger:hover,
.info-trigger--has-data {
  color: #3b82f6;
}

.reported-label {
  font-size: 12px;
  color: #67c23a;
  font-weight: 600;
}

.report-dialog__body {
  font-size: 14px;
  color: #303133;
  line-height: 1.8;
}

.report-dialog__probe {
  margin-top: 10px;
  font-weight: 700;
  font-size: 15px;
  color: #1e293b;
}

.report-dialog__time {
  font-size: 13px;
  color: #909399;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  padding: 0px 0px;
}

.icon-legend {
  padding: 10px;
  background-color: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
}

.icon-legend__title {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin: 0 0 12px 0;
}

.icon-legend__items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
}

.icon-legend__item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-legend__text {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}
</style>

<style>
.el-table .row--no-value {
  background-color: #f0f7ff !important;
}

.el-table .row--no-value:hover > td {
  background-color: #e0efff !important;
}

.event-list-page .el-table--small .el-table__cell {
  padding: 2px 0;
}

.event-list-page .filter-card .el-card__body {
  padding: 5px 10px;
}

.event-list-page .table-card .el-card__body {
  padding: 0px 10px;
}
</style>